import React, { useState } from 'react';
import { Modal } from '@/components/common/Modal';
import { StatusBadge } from '@/components/common/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { evaluationsApi } from '@/services/evaluations.api';
import { EvaluationDetails as EvaluationDetailsType, EvaluationResult } from '@/types';
import { formatTimestamp, formatDuration, getScoreClass, truncateText } from '@/utils/formatters';

interface EvaluationDetailsProps {
  runId: string;
  onClose: () => void;
}

export function EvaluationDetails({ runId, onClose }: EvaluationDetailsProps) {
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [showFailuresOnly, setShowFailuresOnly] = useState(false);
  const [selectedResult, setSelectedResult] = useState<EvaluationResult | null>(null);
  const [modalContent, setModalContent] = useState<{ title: string; content: any } | null>(null);

  const { data: evaluation, isLoading, error } = useApi(
    () => evaluationsApi.get(runId),
    { 
      enabled: !!runId,
      refetchInterval: 5000,
      retryOnError: true, // Retry on 404 errors for newly created evaluations
      maxRetries: 5, // Allow more retries for new evaluations
      stopPollingWhen: (data) => data?.status === 'completed' || data?.status === 'failed', // Stop polling when done
    }
  );

  const handleExportResults = () => {
    if (!evaluation) return;
    
    const dataStr = JSON.stringify(evaluation, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `evaluation_${evaluation.id}_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleCellClick = (title: string, content: any) => {
    setModalContent({ title, content });
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading evaluation details...</p>
        </div>
      </div>
    );
  }

  if (error || !evaluation) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center py-8">
          <p className="text-red-500">Failed to load evaluation details</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const duration = evaluation.completed_at && evaluation.created_at
    ? evaluation.completed_at - evaluation.created_at
    : null;

  // Filter results based on checkboxes
  const filteredResults = evaluation.results?.filter((result) => {
    if (showErrorsOnly && !result.error) return false;
    if (showFailuresOnly && result.score >= 1.0) return false;
    return true;
  }) || [];

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Evaluation Details</h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportResults}
              className="px-3 py-1 text-sm text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
            >
              Export JSON
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Summary Section */}
      <div className="p-6 bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Run ID
            </div>
            <div className="mt-1 text-sm font-semibold text-gray-900" title={evaluation.id}>
              {evaluation.id.substring(0, 8)}...
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Agent
            </div>
            <div className="mt-1 text-sm font-semibold text-gray-900">
              {evaluation.agent_name || '-'}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </div>
            <div className="mt-1">
              <StatusBadge status={evaluation.status} />
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Overall Score
            </div>
            <div className="mt-1">
              {evaluation.overall_score !== undefined && evaluation.overall_score !== null ? (
                <span className={`text-sm font-semibold ${getScoreClass(evaluation.overall_score)}`}>
                  {evaluation.overall_score.toFixed(3)}
                </span>
              ) : (
                <span className="text-sm text-gray-400">-</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Duration
            </div>
            <div className="mt-1 text-sm font-semibold text-gray-900">
              {formatDuration(duration)}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Dataset
            </div>
            <div className="mt-1 text-sm font-semibold text-gray-900" title={evaluation.source_file || 'Direct input'}>
              {evaluation.source_file 
                ? evaluation.source_file.split('/').pop() 
                : 'Manual'
              }
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center space-x-6">
          <label className="flex items-center text-sm">
            <input
              type="checkbox"
              checked={showErrorsOnly}
              onChange={(e) => setShowErrorsOnly(e.target.checked)}
              className="mr-2 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            Show errors only
          </label>
          <label className="flex items-center text-sm">
            <input
              type="checkbox"
              checked={showFailuresOnly}
              onChange={(e) => setShowFailuresOnly(e.target.checked)}
              className="mr-2 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
            />
            Show failures only (score &lt; 1.0)
          </label>
        </div>
      </div>

      {/* Results Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                #
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Input
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ground Truth
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Output
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                Score
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                Duration
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                Error
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredResults.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500 italic">
                  {showErrorsOnly || showFailuresOnly 
                    ? 'No results match the current filters'
                    : 'No results available'
                  }
                </td>
              </tr>
            ) : (
              filteredResults.map((result, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {index + 1}
                  </td>
                  <td 
                    className="px-6 py-4 text-sm text-gray-900 cursor-pointer hover:bg-gray-100 max-w-xs"
                    onClick={() => handleCellClick('Input', result.input)}
                  >
                    <div className="truncate font-mono text-xs">
                      {truncateText(result.input, 100)}
                    </div>
                  </td>
                  <td 
                    className="px-6 py-4 text-sm text-gray-900 cursor-pointer hover:bg-gray-100 max-w-xs"
                    onClick={() => handleCellClick('Ground Truth', result.ground_truth)}
                  >
                    <div className="truncate font-mono text-xs">
                      {truncateText(result.ground_truth, 100)}
                    </div>
                  </td>
                  <td 
                    className="px-6 py-4 text-sm text-gray-900 cursor-pointer hover:bg-gray-100 max-w-xs"
                    onClick={() => handleCellClick('Output', result.output)}
                  >
                    <div className="truncate font-mono text-xs">
                      {truncateText(result.output, 100)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`text-sm font-semibold ${getScoreClass(result.score)}`}>
                      {result.score.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                    {formatDuration(result.duration)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {result.error ? (
                      <span 
                        className="text-red-600 cursor-pointer hover:text-red-800 text-sm"
                        onClick={() => handleCellClick('Error', result.error)}
                        title={result.error}
                      >
                        ⚠️
                      </span>
                    ) : (
                      <span className="text-green-600 text-sm">✓</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* JSON Preview Modal */}
      {modalContent && (
        <Modal
          isOpen={!!modalContent}
          onClose={() => setModalContent(null)}
          title={modalContent.title}
          size="lg"
        >
          <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
            <pre className="text-sm whitespace-pre-wrap font-mono max-h-96 overflow-y-auto">
              {typeof modalContent.content === 'string' 
                ? modalContent.content 
                : JSON.stringify(modalContent.content, null, 2)
              }
            </pre>
          </div>
        </Modal>
      )}
    </div>
  );
}